import { useContext } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography
} from "@mui/material";
import MatchedOrderAddress, { Property } from "./MatchedOrderAddress";
import { OrderContext } from "../../contexts/OrderDataContext";
import MatchedAttorney from "./MatchedAttorney";
import MatchedParty from "./MatchedParty";
import { Buyer, MatchedOrder } from "./MatchedOrder";
import { MatchedOldOrder } from "./MatchedOldOrder";
import MatchedPriorPolicy from "./MatchedPriorPolicy";
import MatchedPrincipal from "./MatchedPrincipal";
import Iconify from "../../../minimals/components/iconify";
import { OTSOrder } from "../../contexts/types/types";
import { mapOTSOrderToMatchedOrder } from "./mapOTSOrderToMatchedOrder";

type OrderAccordionProps = {
  order?: MatchedOrder;
  matchedOldOrder?: MatchedOldOrder;
  matchedOTSOrder?: OTSOrder;
  imported: boolean;
  relatedOrdersElement: HTMLElement | null;
  isOldOrder?: boolean;
  expanded: boolean;
};

export default function OrderAccordion(props: OrderAccordionProps) {
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  const { relatedOrders, setRelatedOrders } = useContext(OrderContext);
  const { order, matchedOldOrder, matchedOTSOrder, imported, relatedOrdersElement, expanded } = props;

  const handleImport = (fileNumber: string) => {
    if (imported) return;

    const newEntry = { fileNumber, fileNumberList: [], notes: null };
    const updatedOrders = relatedOrders[0]?.fileNumber ? [...relatedOrders, newEntry] : [newEntry];

    setRelatedOrders(updatedOrders);
    relatedOrdersElement?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const renderHeader = (title: string, subtitle?: string) => (
    <Grid container>
      <Grid item xs={12}>
        <Typography>
          <Box sx={{ fontWeight: 600, display: "inline-block" }}>{title}</Box>
        </Typography>
        {subtitle && <Typography variant="caption">{subtitle}</Typography>}
      </Grid>
    </Grid>
  );

  const renderImportLink = (fileNumber: string) => (
    <Typography color="primary.main">
      {imported && <Iconify fontSize={16} color="success.main" icon="eva:checkmark-fill" />}
      <Link
        onClick={() => handleImport(fileNumber)}
        sx={{ cursor: "pointer" }}
        underline={imported ? "none" : "always"}
      >
        {imported ? "Imported" : "Import"}
      </Link>{" "}
      {imported ? "as related order" : 'into "Related"'}
    </Typography>
  );

  const renderExternalLink = (url: string) => (
    <IconButton onClick={() => window.open(url, "_blank")?.focus()} size="small">
      <Iconify icon="eva:external-link-fill" />
    </IconButton>
  );

  const renderCommonDetails = (mappedOrder: MatchedOrder) => (
    <>
      <MatchedOrderAddress order={mappedOrder} />
      {mappedOrder.principal?.name && <MatchedPrincipal principal={mappedOrder.principal} />}
      {mappedOrder.buyers?.attorney?.name && <MatchedAttorney contact={mappedOrder.buyers.attorney} />}
      {mappedOrder.buyers?.buyers.map((buyer, i) => (
        <MatchedParty key={i} party={buyer} />
      ))}
      {mappedOrder.priorPolicy && (
        <MatchedPriorPolicy priorPolicy={mappedOrder.priorPolicy} orderNumber={mappedOrder.number} />
      )}
    </>
  );

  if (order) {
    const titleCode = order.titleCode?.toUpperCase();
    const title = titleCode ? `${titleCode}-${order.number}` : order.number;

    return (
      <Accordion
        defaultExpanded={expanded}
        key={order.identifier}
        sx={{ "&:before": { backgroundColor: "transparent" } }}
      >
        <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
          {renderHeader(title, order.transactionType)}
        </AccordionSummary>
        <AccordionDetails>
          <Box pb={2}>
            <Grid container mb={2}>
              <Grid item xs={11}>
                {renderImportLink(order.number)}
              </Grid>
              <Grid item xs={1}>
                <Stack direction="row" justifyContent="end">
                  {renderExternalLink(`${momentumUrl}new-home/${order.number}`)}
                </Stack>
              </Grid>
            </Grid>
            {renderCommonDetails(order)}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  if (matchedOldOrder) {
    return (
      <Accordion
        defaultExpanded={expanded}
        key={matchedOldOrder.orderNumber + matchedOldOrder.orderDate}
        sx={{ "&:before": { backgroundColor: "transparent" } }}
      >
        <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
          {renderHeader(matchedOldOrder.orderNumber.toUpperCase(), "Old Order")}
        </AccordionSummary>
        <AccordionDetails>
          <Box pb={2}>
            <Grid container mb={2}>
              <Grid item xs={11}>
                {renderImportLink(matchedOldOrder.orderNumber)}
              </Grid>
              <Grid item xs={1}>
                <Stack direction="row" justifyContent="end">
                  {renderExternalLink(`${momentumUrl}new-home/${matchedOldOrder.orderNumber}`)}
                </Stack>
              </Grid>
            </Grid>
            <Property
              address1={matchedOldOrder.address}
              address2=""
              city={matchedOldOrder.city}
              state={matchedOldOrder.state}
              zip={matchedOldOrder.zip}
              county=""
              propertyUse=""
              section=""
              block=""
              lots={[]}
              parcelIds={[]}
              legalDescription={matchedOldOrder.legalDescription}
            />
            {matchedOldOrder.buyer && (
              <MatchedParty isOldOrder party={{ individual: { firstName: matchedOldOrder.buyer } } as Buyer} />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  if (matchedOTSOrder) {
    const mappedOrder = mapOTSOrderToMatchedOrder(matchedOTSOrder);
    return (
      <Accordion
        defaultExpanded={expanded}
        key={matchedOTSOrder.id}
        sx={{ "&:before": { backgroundColor: "transparent" } }}
      >
        <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
          {renderHeader(`OTS - ${matchedOTSOrder.orderInfo.orderNumber}`, matchedOTSOrder.orderInfo.transactionType)}
        </AccordionSummary>
        <AccordionDetails>
          <Box pb={2}>
            <Grid container mb={2}>
              <Grid item xs={11} />
              <Grid item xs={1}>
                <Stack direction="row" justifyContent="end">
                  {renderExternalLink(`https://mcres-ots.netlify.app/documents?id=${matchedOTSOrder.id}`)}
                </Stack>
              </Grid>
            </Grid>
            {renderCommonDetails(mappedOrder)}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  return null;
}
